import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { exhaustMap, from, interval, concatMap, throwError, of, takeWhile, takeUntil } from "rxjs";

import {
  CompletionRequirement,
  ProcessingStates,
  OrganizationApiKeyCompletionRequirement,
} from "graphql_globals";
import { useA11y } from "common/accessibility";
import Env from "config/environment";
import { searchParamsToObject } from "util/location";
import { useUnmountSubject } from "util/rxjs/hooks";
import { captureException } from "util/exception";
import { unsignedBundleRedirect } from "redux/actions/authentication";
import { useNavigateToBundlePrepareRoute } from "util/routes";
import { useAppDispatch } from "redux/hooks";
import { EXTERNAL_DATA_SEARCH_PARAM } from "constants/click_tracking";
import { useMutation } from "util/graphql";

import UpdateCustomerProfileMutation from "./update_customer_profile_mutation.graphql";

const MESSAGES = defineMessages({
  titleNotarization: {
    id: "5dc1a2ea-6bf5-4abb-be35-907a32d751ae",
    defaultMessage: "Upload a document to notarize - Proof",
  },
  titleEsign: {
    id: "dd094f7c-fb1a-4b0c-abd0-465990905e75",
    defaultMessage: "Upload a document to eSign - Proof",
  },
  titleIdentify: {
    id: "00093273-b2d4-4519-9711-899375c7671b",
    defaultMessage: "Verify your identity - Proof",
  },
});

const API_URL = `${Env.apiHost}/v1/transactions`;

const DOC_STATE_POLL_INTERVAL = 1500;
// This poller polls the transaction through the Partner API
// instead of using the DocumentStatePoller component like elsewhere in the app
// because of authorization issues.
function waitForPendingDocuments(
  transactionId: string,
  apiKey: string,
  unmount$: ReturnType<typeof useUnmountSubject>,
) {
  return new Promise<void>((resolve, reject) => {
    interval(DOC_STATE_POLL_INTERVAL)
      .pipe(
        exhaustMap(() =>
          from(
            fetch(`${API_URL}/${transactionId}/processing_state`, {
              method: "GET",
              headers: { ApiKey: apiKey },
            }),
          ),
        ),
        concatMap((response) => {
          return response.ok
            ? from(response.json().then((data) => data.documents as { processing_state: string }[]))
            : throwError(() => new Error("document failed to upload"));
        }),
        concatMap((documents) => {
          const procStates = documents.map((doc) => doc.processing_state.toUpperCase());
          if (procStates.includes(ProcessingStates.FAILED)) {
            return throwError(() => new Error("document failed to upload"));
          } else if (procStates.includes(ProcessingStates.PENDING)) {
            return of(ProcessingStates.PENDING);
          }
          return of(ProcessingStates.DONE);
        }),
        takeWhile((status) => status === ProcessingStates.PENDING),
        takeUntil(unmount$),
      )
      .subscribe({ complete: resolve, error: reject });
  });
}

type DocumentResourceParam = string | { resource: string };
export type DocumentParam = {
  resource: DocumentResourceParam;
  filename?: string;
  bundle_position?: number | null;
};
export type SignerParam = {
  user_id?: string;
  email: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  pronouns?: string;
  phone_number?: string;
  suppress_communications?: "all";
};
type TransactionData = { id: string };
type V1CreateTransactionParams = {
  signers?: never;
  documents?: never;
};
type V2CreateTransactionParams = {
  signers: SignerParam[];
  documents: DocumentParam[];
};
type CreateTransactionParams = {
  onCreate?: (data: TransactionData) => void;
  onReady: (data: TransactionData) => Promise<unknown>;
  onError: (errorMessage: string) => void;
  draft: boolean;
  suppressEmail?: boolean;
} & (V1CreateTransactionParams | V2CreateTransactionParams);
export function useEasylinkCreateTransaction(easylinkApiKey: string) {
  const unmount$ = useUnmountSubject();
  const updateCustomerProfileMutateFn = useMutation(UpdateCustomerProfileMutation);
  const { apiKey, createParams } = useEasylinkSearchParams();
  const currentApiKey = easylinkApiKey || apiKey;
  return useCallback(
    async ({
      onCreate,
      onReady,
      onError,
      draft,
      signers,
      documents,
      suppressEmail,
    }: CreateTransactionParams) => {
      try {
        const body: Record<string, unknown> = createParams;
        body.suppress_email = suppressEmail;
        body.require_new_signer_verification = false;
        body.draft = draft;
        body.creation_source = "easylink";
        if (signers) {
          body.signers = signers;
        }
        if (documents) {
          body.documents = documents;
        }
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            ApiKey: currentApiKey,
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(body),
        });
        const data = await response.json();

        const updateProfilePromises =
          signers?.map((signer) => {
            if (signer.user_id) {
              return updateCustomerProfileMutateFn({
                variables: {
                  mutationInput: {
                    acceptedTos: true,
                    id: signer.user_id,
                    firstName: signer.first_name,
                    middleName: signer.middle_name,
                    lastName: signer.last_name,
                    pronouns: signer.pronouns,
                  },
                },
              });
            }
            return Promise.resolve();
          }) || [];

        await Promise.all(updateProfilePromises);

        if (response.ok) {
          onCreate?.(data);

          if (data.documents.length > 0) {
            await waitForPendingDocuments(data.id, currentApiKey, unmount$);
          }

          await onReady(data);
        } else {
          // either there's a top level access error or a lower level processing error
          const errorString = data.message ? data.message : data.errors.join(", ");
          onError(
            `There were errors prepping the notarization: ${errorString}. Please contact support from the website that referred you here.`,
          );
        }
      } catch (error) {
        captureException(error);
        onError(`An unexpected error occurred: ${(error as Error).message}`);
      }
    },
    [currentApiKey, createParams],
  );
}

/** for use in components that only support notarize/esign */
export function getCompletionRequirementFromEasylinkRequirement(
  easylinkRequirement: OrganizationApiKeyCompletionRequirement,
) {
  switch (easylinkRequirement) {
    case OrganizationApiKeyCompletionRequirement.ESIGN:
      return CompletionRequirement.ESIGN;
    case OrganizationApiKeyCompletionRequirement.PROOFING:
      return CompletionRequirement.PROOFING;
    default:
      return CompletionRequirement.NOTARIZATION;
  }
}

/** for use in components that only support notarize/esign */
export function getEasylinkRequirementFromCompletionRequirement(
  completionRequirement: CompletionRequirement,
) {
  switch (completionRequirement) {
    case CompletionRequirement.ESIGN:
      return OrganizationApiKeyCompletionRequirement.ESIGN;
    case CompletionRequirement.PROOFING:
      return OrganizationApiKeyCompletionRequirement.PROOFING;
    default:
      return OrganizationApiKeyCompletionRequirement.NOTARIZATION;
  }
}

function getTitleFromEasylinkRequirement(
  easylinkRequirement: OrganizationApiKeyCompletionRequirement,
) {
  switch (easylinkRequirement) {
    case OrganizationApiKeyCompletionRequirement.ESIGN:
      return MESSAGES.titleEsign;
    case OrganizationApiKeyCompletionRequirement.IDENTIFY:
    case OrganizationApiKeyCompletionRequirement.VERIFY:
      return MESSAGES.titleIdentify;
    default:
      return MESSAGES.titleNotarization;
  }
}
export function useEasylinkTitle({
  easylinkRequirement,
}: {
  easylinkRequirement: OrganizationApiKeyCompletionRequirement;
}) {
  const a11y = useA11y();
  const intl = useIntl();

  a11y.useDocumentEntitler({
    title: intl.formatMessage(getTitleFromEasylinkRequirement(easylinkRequirement)),
  });
}

const BOOLEAN_PARAMS = ["allow_signer_annotations", "pdf_bookmarked", "require_secondary_photo_id"];
type CreateParams = {
  [key: string]: unknown;
  documents?: DocumentResourceParam[];
};
export function useEasylinkSearchParams() {
  const queryObject = searchParamsToObject(useSearchParams()[0]);

  const createParams: CreateParams = {};
  Object.entries(queryObject).forEach(([k, v]) => {
    // NOTE: email purposefully excluded to keep consistent experience with
    // other guest upload behaviors (i.e. don't make a full transaction, only a draft)
    if (k === "signer[email]") {
      return;
    }

    createParams[k] = v;
  });

  // This section solely for backwards compatibility
  // Since we used to have accept some other params before it was standardized
  const paramNameMap = {
    // email purposefully excluded, as with above
    // email: "signer[email]",
    externalId: "external_id",
    transactionType: "transaction_type",
    transactionName: "transaction_name",
    messageToSigner: "message_to_signer",
    allowSignerAnnotations: "allow_signer_annotations",
  };
  (Object.keys(paramNameMap) as (keyof typeof paramNameMap)[]).forEach((k) => {
    const value = queryObject[k];
    if (value) {
      createParams[paramNameMap[k]] = value;
    }
  });

  // cast boolean params to boolean from string
  BOOLEAN_PARAMS.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(createParams, k)) {
      createParams[k] = createParams[k] === "true";
    }
  });

  const docList: DocumentResourceParam[] = [];
  ["document", "documents"].forEach((d) => {
    const value = queryObject[d];
    if (typeof value === "string") {
      docList.push(value);
    } else if (value) {
      docList.push(...value);
    }
  });
  if (docList.length) {
    createParams.documents = docList;
    // we need to remove the document accessor here
    // because this would end up in the createTransaction call
    // and throw an error if documents and document are sentß
    delete createParams.document;
  }
  delete createParams[EXTERNAL_DATA_SEARCH_PARAM];
  delete createParams.apiKey;
  delete createParams.ApiKey;
  delete createParams.autoLogin;

  return {
    apiKey:
      (queryObject.ApiKey as string | undefined) ||
      // Backwards compatibility--ApiKey is preferred version (it matches partner api)
      // but previous version used apiKey
      (queryObject.apiKey as string | undefined) ||
      Env.easyLinkApiKey ||
      "",
    email: (queryObject["signer[email]"] || queryObject.email) as string | undefined,
    autoLogin: queryObject.autoLogin && (queryObject.autoLogin as string).toLowerCase() === "true",
    externalData: queryObject[EXTERNAL_DATA_SEARCH_PARAM] as string | undefined,
    createParams,
    linkClickId: queryObject.link_click_id as string | undefined,
  };
}

export function useEasylinkRedirectToBundle() {
  const dispatch = useAppDispatch();
  const navigateToBundlePrepare = useNavigateToBundlePrepareRoute();
  return useCallback((bundleId: string) => {
    navigateToBundlePrepare({ bundleId, replace: true });
    dispatch(unsignedBundleRedirect(false));
  }, []);
}

export function externalToInternalGid(externalGid: string) {
  // GIDs come back from partner api in `ot_xxxxx` format,
  // but internal api uses `otxxxxx`
  return externalGid.replace("_", "");
}
